<template>
  <div class="manage-ticket">
    <page-header
      :actions="headerActions"
      :has-route="header.hasRoute"
      :page="header.page"
    />
    <div
      v-if="isReadOnly && ticket.statusId == caseStatus.Resuelto"
      class="resolved-comment"
    >
      <icon-view
        class="icon-view_extended"
        icon="fas fa-check-circle"
        title="Comentario de Solicitud Resuelta"
      />
      <p class="mb-0 ml-4 mt-2">
        {{ ticket.caseSolution }}
      </p>
    </div>

    <div
      v-if="isReadOnly && ticket.statusId == caseStatus.Cancelado"
      class="canceled-comment"
    >
      <icon-view
        class="icon-view_extended"
        icon="fas fa-times-circle"
        title="Comentario de Cancelación"
      />
      <p class="mb-0 ml-4 mt-2">
        {{ ticket.comment }}
      </p>
    </div>

    <section-title title="Describa con detalles el apoyo a solicitar" />

    <validation-observer ref="refHeaderValidation">
      <validation-provider v-slot="{ errors }" rules="required">
        <custom-text-area
          id="description"
          v-model="ticket.description"
          class="mb-3"
          :count-classes="['d-block text-right']"
          custom-error-msg="La descripción es requerida."
          :disabled="isReadOnly"
          :error="errors[0]"
          :maxlength="3000"
          name="description"
          placeholder="Escriba aquí"
          show-char-count
        />
      </validation-provider>

      <template v-if="!isReadOnly || (isReadOnly && hasDocument)">
        <div class="row mb-4">
          <div class="col-md-6 col-xs-12">
            <label for="">Documentos</label>
            <div
              v-for="(doc, index) in documents"
              :key="doc.tempId"
              class="row mt-1"
            >
              <div class="col-11 pr-0">
                <custom-file-input
                  :ref="`fileUpload-${doc.tempId}`"
                  v-model="ticket.documents[index].data"
                  :accept="allowedFiles"
                  :attachment-id="doc.id"
                  :disabled="isReadOnly"
                  display-style="normal"
                  :file-extension-error-msg="customFileExtensionErrorMsg"
                  label-position="left"
                  :max-size="10"
                  :name="doc.name"
                  :on-download="onDownload"
                />
              </div>
              <div class="col p-0">
                <button
                  v-if="
                    ticket.documents.length > 1 &&
                      !ticket.documents[index].data &&
                      !isReadOnly
                  "
                  class="btn btn-outline-danger"
                  :class="{ 'input-disabled': disabled }"
                  :disabled="disabled"
                  :hidden="disabled"
                  style="width: 37px; height: 37px; padding: 0"
                  type="button"
                  @click="removeDocumentRow(doc.tempId)"
                >
                  <i class="fas fa-times-circle" />
                </button>
              </div>
            </div>
            <div class="mt-2">
              <action-button
                v-if="!isReadOnly"
                class="mt-auto"
                i="fas fa-file-plus pr-1"
                text="Añadir"
                variant="success"
                @click="addDocumentRow"
              />
            </div>
          </div>

          <div class="col-12">
            <small class="data-label allowed-files-message">
              El sistema le permitirá anejar la evidencia con los siguientes
              formatos: .jpg, .png, .docx, .doc, .xlsx, .xls, .pdf, .ppt .pptx.
            </small>
          </div>
        </div>
      </template>

      <section-title
        title="Si la situacion es referente a una pantalla en particular, indique el módulo y pantalla"
      />
      <div class="row mb-4">
        <div class="col-xs-6 col-sm-4">
          <custom-drop-down
            id="module"
            v-model="ticket.module"
            :disabled="isReadOnly"
            fieldkey="id"
            fieldname="name"
            label="Módulo:"
            :options="modules"
            placeholder="Seleccione"
            @input="onModuleChange"
          />
        </div>
        <div class="col-xs-6 col-sm-4">
          <custom-drop-down
            id="screen"
            v-model="ticket.screen"
            :disabled="isReadOnly || screens.length === 0"
            fieldkey="id"
            fieldname="name"
            label="Pantalla:"
            :options="screens"
            placeholder="Seleccione"
            @input="onScreenChange"
          />
        </div>
      </div>
    </validation-observer>

    <section-title
      title="Favor de documentar un número de referencia relacionado a la solicitud de apoyo que nos ayude a obtener información con mayor exactitud"
    />

    <validation-observer ref="refReferenceNumberAdded">
      <div class="frame-container mb-4">
        <div class="section-label mb-3">Número de Referencia</div>

        <template v-if="isReadOnly">
          <div class="row">
            <custom-chips-group
              class="refNumbers"
              disabled
              no-background
              object-id="id"
              object-text="referenceValue"
              object-title="referenceTypeName"
              :on-item-deleted="onReferenceNumberTypeDeleted"
              :selected-items="flatReferenceNumberTypes"
              title=""
            />
          </div>
        </template>

        <template v-else>
          <div class="row">
            <div class="col-xs-6 col-sm-4">
              <validation-provider
                v-slot="{ errors }"
                mode="passive"
                rules="required"
              >
                <custom-drop-down
                  id="screen"
                  v-model="newReferenceNumberType.referenceType"
                  custom-error-msg="El tipo de referencia es requerido."
                  :disabled="isReadOnly"
                  :error="errors[0]"
                  fieldkey="id"
                  fieldname="name"
                  label="Tipo de Referencia:"
                  :options="referenceNumberTypes"
                  placeholder="Seleccione"
                />
              </validation-provider>
            </div>
            <div v-if="showSieDD" class="col-xs-6 col-sm-4">
              <validation-provider
                v-slot="{ errors }"
                mode="passive"
                rules="required"
              >
                <custom-drop-down
                  id="sie"
                  v-model="newReferenceNumberType.student"
                  custom-error-msg="La selección de un estudiante es requerido."
                  :error="errors[0]"
                  fieldkey="id"
                  fieldname="name"
                  label="Valor:"
                  :options="studentsListFiltered"
                  placeholder="Seleccione"
                />
              </validation-provider>
            </div>
            <div v-else class="col-xs-6 col-sm-4">
              <validation-provider
                v-slot="{ errors }"
                mode="passive"
                name="Valor"
                :rules="valueValidations"
              >
                <custom-input
                  id="referenceNumber"
                  :key="valueMaskType"
                  v-model="newReferenceNumberType.referenceValue"
                  :disabled="isReadOnly"
                  :error="errors[0]"
                  label="Valor:"
                  :mask-type="valueMaskType"
                  :masked="valueMaskType != null"
                  type="text"
                />
              </validation-provider>
            </div>

            <template v-if="!isReadOnly">
              <div class="col-4 d-flex btn-referencenumber-content">
                <action-button
                  class="mt-auto"
                  i="fas fa-file-plus pr-1"
                  text="Añadir"
                  variant="success"
                  @click="onReferenceNumberTypeAdded"
                />
              </div>
            </template>
          </div>

          <div
            v-if="hasNotReferenceNumberType || hasReferencesValidationErrors"
            class="row"
          >
            <div class="mt-3 ml-3 font-weight-normal font-14 color-secondary-6">
              <error
                :key="validateTypeRefMessage"
                :message="validateTypeRefMessage"
                variant="no-border"
              />
            </div>
          </div>

          <div v-if="referenceValueExists" class="row">
            <div class="mt-3 ml-3 font-weight-normal font-14 color-secondary-6">
              <error
                message="El valor de la referencia ya existe."
                variant="no-border"
              />
            </div>
          </div>

          <div v-if="ticket.referenceNumberTypes.length > 0">
            <separator
              class="separator_extended"
              text="Números de referencia añadidos"
            />
            <div class="row">
              <custom-chips-group
                class="refNumbers"
                :disabled="isReadOnly"
                no-background
                object-id="id"
                object-text="referenceValue"
                object-title="referenceTypeName"
                :on-item-deleted="onReferenceNumberTypeDeleted"
                :selected-items="flatReferenceNumberTypes"
                title=""
              />
            </div>
          </div>
        </template>
      </div>
    </validation-observer>
    <section-title title="Favor de verificar su información de contacto" />
    <validation-observer ref="refFooterValidation">
      <div class="row">
        <div class="col-sm-4 col-xs-12">
          <validation-provider
            v-slot="{ errors, failedRules }"
            rules="required|email"
          >
            <custom-input
              id="email"
              v-model="ticket.email"
              :custom-error-msg="
                failedRules.required
                  ? 'El correo electrónico es requerido.'
                  : 'El correo electrónico no es válido.'
              "
              :disabled="isReadOnly"
              :error="errors[0]"
              label="Correo Electrónico:"
              type="text"
            />
          </validation-provider>
        </div>
        <div class="col-sm-4 col-xs-12">
          <div class="row">
            <div class="col-md-8">
              <validation-provider v-slot="{ errors }" rules="required">
                <custom-input
                  id="telephone1"
                  v-model="ticket.telephone1"
                  :custom-error-msg="'El teléfono 1 es requerido.'"
                  :disabled="isReadOnly"
                  :error="errors[0]"
                  label="Teléfono 1:"
                  mask-type="Phone"
                  type="text"
                />
              </validation-provider>
            </div>
            <div class="col-md-4">
              <custom-input
                id="telephone1"
                v-model="ticket.telephone1Extension"
                :disabled="isReadOnly"
                label="Ext.:"
                mask-type="PhoneExt"
                type="text"
              />
            </div>
          </div>
        </div>
        <div class="col-sm-4 col-xs-12">
          <div class="row">
            <div class="col-md-8">
              <custom-input
                id="telephone2"
                v-model="ticket.telephone2"
                :disabled="isReadOnly"
                label="Teléfono 2:"
                mask-type="Phone"
                type="text"
              />
            </div>
            <div class="col-md-4">
              <custom-input
                id="telephone2"
                v-model="ticket.telephone2Extension"
                :disabled="isReadOnly"
                label="Ext.:"
                mask-type="PhoneExt"
                type="text"
              />
            </div>
          </div>
        </div>
      </div>
    </validation-observer>
  </div>
</template>

<script>
export const referenceTypes = Object.freeze({
  NumeroDeSIE: 1,
  NumeroPEI: 3,
  NumeroContratoRP: 11,
  NumeroDeCITA: 12,
  NumeroDeREFERIDO: 31,
  NumeroDeFACTURA: 4,
  Mes: 23,
  NombreEspecialista: 21,
  Disciplina: 33,
  Servicio: 52,
  numeroDeSolicitud: 10,
  NumeroQuerella: 1042,
  NumeroConsulta: 1043,
  DisciplinaEvaluacion: 1044,
});

export const screens = Object.freeze({
  FinalizacionPeriodo: 66,
  HistorialFactura: 67,
  ManejoCitas: 69,
  ManejoReferidos: 81,
  ReferidosAsistenciaTecnologica: 10,
  VigenciaReferidos: 89,
  DiscucionDeEvaluaciones: 12,
  Evaluaciones: 15,
  SolicitudEvaluacionAdicional: 13,
  ManejoDeCitas: 18,
  ManejoDeCoordinacionEvaluacion: 18,
  HistorialDeCitas: 29,
  SolicitudDeHorasCompensatorias: 88,
  SolicitudServiciosAsistenciaTecnologica: 129,
  CuestionarioPEIControversia: 296,
  ImprimirReportePEIoPS: 297,
  ImprimirReporteReferidos: 298,
  HistorialComentarios: 299,
  AñadirComentario: 300,
  VerDocumento: 301,
  Historial_Terapias: 302,
  DirectorioProveedores: 303,
  CrearNuevaSolicitud: 304,
  Entrar: 305,
  ImprimirSolicitud: 306,
  ImprimirCartaAprobacion: 307,
  VerHistorialContratos: 308,
  Historial_RegistroVisitas: 314,
});

export const modules = Object.freeze({
  PEI: 6,
  Estudiante: 2,
  RecursosHumanos: 7,
  PEI_Padres: 297,
  Evaluaciones: 298,
  HistorialTerapias: 299,
  SolicitudRP: 300,
  HistorialContrato: 301,
  ReferidosEvaluaciones: 302,
  HistorialQuerellas: 303,
  HistorialRegistroVisitas: 304,
  AsistenciaBECA: 305,
  HistorialConsultaUbicacion: 306,
});

import PageHeader from 'PageHeader';
import SectionTitle from 'SectionTitle';
import CustomTextArea from 'CustomTextArea';
import CustomFileInput from 'CustomFileInput';
import CustomDropDown from 'custom-drop-down';
import ActionButton from 'ActionButton';
import CustomInput from 'CustomInput';
import CustomChipsGroup from 'CustomChipsGroup';
import Error from 'custom-error';
import alert from 'utils/mixins/alert.mixin.js';
import Separator from 'custom-separator';
import IconView from 'icon-view';
import caseStatus from '@/utils/constants/ticketCaseStatus.js';
import application from '@/utils/constants/ticketApplications.js';

export default {
  name: 'ManageTicket',
  components: {
    PageHeader,
    SectionTitle,
    CustomTextArea,
    CustomFileInput,
    CustomDropDown,
    ActionButton,
    CustomInput,
    CustomChipsGroup,
    Error,
    Separator,
    IconView,
  },
  mixins: [alert],
  props: {
    ticketData: {
      type: Object,
      default: () => {},
    },
    modules: {
      type: Array,
      required: true,
    },
    referenceNumberTypes: {
      type: Array,
      required: true,
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
    onDownload: {
      type: Function,
      default: () => Promise.resolve({}),
    },
    user: {
      type: Object,
      default: () => {},
    },
    saveTicket: {
      type: Function,
      default: () => Promise.resolve({}),
    },
    validateReferenceNumber: {
      type: Function,
      default: () => Promise.resolve({}),
    },
    application: {
      type: [String, Number],
      default: () => null,
    },
    studentsList: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      caseStatus,
      ticket: {
        itemId: null,
        description: null,

        documents: [
          {
            data: null,
            id: null,
            name: null,
          },
        ],
        module: null,
        screen: null,
        referenceNumberTypes: [],
        email: null,
        telephone1: null,
        telephone1Extension: null,
        telephone2: null,
        telephone2Extension: null,
        statusId: null,
        workflowId: null,
      },
      hasNotReferenceNumberType: false,
      hasReferencesValidationErrors: false,
      referenceValueExists: false,
      newReferenceNumberType: {
        id: null,
        referenceType: null,
        referenceValue: null,
      },
      header: {
        hasRoute: false,
        page: {
          icon: 'fas fa-user-circle',
          title: 'Mesa de Apoyo',
          routes: ['Mesa de Apoyo'],
        },
      },
      allowedFiles: '.jpg,.png,.docx,.doc,.xlsx,.xls,.pdf,.ppt,.pptx',
      customFileExtensionErrorMsg: 'El formato no es válido.',
    };
  },
  computed: {
    documents() {
      return this.ticket.documents.map((x) => {
        if (!x.tempId && x.id) x.tempId = x.id;
        return x;
      });
    },
    valueValidations() {
      if (this.valueLength != null) {
        return { required: true, length: this.valueLength };
      }
      return { required: true };
    },
    valueMaskType() {
      if (this.newReferenceNumberType.referenceType == undefined) {
        return null;
      }
      switch (this.newReferenceNumberType.referenceType.id) {
        case referenceTypes.NumeroDeSIE:
          return 'SieOrSSN';
        case referenceTypes.NumeroDeCITA:
          return 'AppointmentNumber';
        case referenceTypes.NumeroDeREFERIDO:
          return 'ReferralNumber';
        case referenceTypes.NumeroDeFACTURA:
          return 'BillNumber';
        default:
          return null;
      }
    },
    valueLength() {
      if (this.newReferenceNumberType.referenceType == undefined) {
        return null;
      }
      switch (this.newReferenceNumberType.referenceType.id) {
        case referenceTypes.NumeroDeSIE:
          return 8;
        case referenceTypes.NumeroDeCITA:
          return 11;
        case referenceTypes.NumeroDeREFERIDO:
          return 14;
        case referenceTypes.NumeroDeFACTURA:
          return 15;
        default:
          return null;
      }
    },
    canContinueValidation() {
      return !this.ticket.screen || !this.ticket.module;
    },
    peiNumberIsRequired() {
      if (this.canContinueValidation) {
        return;
      }

      return (
        this.ticket.module.id == modules.PEI_Padres &&
        !this.ticket.referenceNumberTypes.some(
          (c) => c.referenceType.id == referenceTypes.NumeroPEI
        )
      );
    },
    contractNumberRPRequired() {
      if (this.canContinueValidation) {
        return;
      }

      return (
        this.ticket.module.id == modules.HistorialContrato &&
        !this.ticket.referenceNumberTypes.some(
          (c) => c.referenceType.id == referenceTypes.NumeroContratoRP
        )
      );
    },
    serviceRequired() {
      if (this.canContinueValidation) {
        return;
      }

      return (
        this.ticket.module.id == modules.AsistenciaBECA &&
        !this.ticket.referenceNumberTypes.some(
          (c) => c.referenceType.id == referenceTypes.Servicio
        )
      );
    },
    consultaNumberRequired() {
      if (this.canContinueValidation) {
        return;
      }

      return (
        this.ticket.module.id == modules.HistorialConsultaUbicacion &&
        !this.ticket.referenceNumberTypes.some(
          (c) => c.referenceType.id == referenceTypes.NumeroConsulta
        )
      );
    },
    sieIsrequired() {
      if (this.canContinueValidation) {
        return;
      }

      return (
        ![
          screens.DiscucionDeEvaluaciones,
          screens.Evaluaciones,
          screens.SolicitudEvaluacionAdicional,
          screens.ManejoDeCitas,
          screens.ManejoDeCoordinacionEvaluacion,
          screens.HistorialDeCitas,
          screens.ManejoReferidos,
          screens.ReferidosAsistenciaTecnologica,
          screens.SolicitudDeHorasCompensatorias,
          screens.SolicitudServiciosAsistenciaTecnologica,
          screens.DirectorioProveedores,
          screens.CrearNuevaSolicitud,
          screens.Entrar,
          screens.ImprimirSolicitud,
          screens.ImprimirCartaAprobacion,
          screens.VerHistorialContratos,
        ].includes(this.ticket.screen.id) &&
        this.ticket.module.id != modules.HistorialContrato &&
        this.ticket.module.id != modules.HistorialQuerellas &&
        this.ticket.module.id != modules.HistorialRegistroVisitas &&
        this.ticket.module.id != modules.HistorialConsultaUbicacion &&
        !this.ticket.referenceNumberTypes.some(
          (c) => c.referenceType.id == referenceTypes.NumeroDeSIE
        )
      );
    },
    isDisciplineRequired() {
      if (this.canContinueValidation) {
        return;
      }
      return (
        [
          screens.DiscucionDeEvaluaciones,
          screens.Evaluaciones,
          screens.SolicitudEvaluacionAdicional,
          screens.VerDocumento,
        ].includes(this.ticket.screen.id) &&
        (this.ticket.module.id === modules.Estudiante ||
          this.ticket.module.id === modules.Evaluaciones) &&
        !this.ticket.referenceNumberTypes.some(
          (c) => c.referenceType.id == referenceTypes.Disciplina
        )
      );
    },
    isEvaluationDisciplineRequired() {
      if (this.canContinueValidation) {
        return;
      }

      return (
        this.ticket.module.id == modules.ReferidosEvaluaciones &&
        !this.ticket.referenceNumberTypes.some(
          (c) => c.referenceType.id == referenceTypes.DisciplinaEvaluacion
        )
      );
    },
    isQuerellaNumberRequired() {
      if (this.canContinueValidation) {
        return;
      }

      return (
        this.ticket.module.id == modules.HistorialQuerellas &&
        !this.ticket.referenceNumberTypes.some(
          (c) => c.referenceType.id == referenceTypes.NumeroQuerella
        )
      );
    },
    isNumberDateRequired() {
      if (this.canContinueValidation) {
        return;
      }
      return (
        [
          screens.ManejoDeCitas,
          screens.ManejoDeCoordinacionEvaluacion,
          screens.HistorialDeCitas,
        ].includes(this.ticket.screen.id) &&
        this.ticket.module.id === modules.Estudiante &&
        !this.ticket.referenceNumberTypes.some(
          (c) => c.referenceType.id == referenceTypes.NumeroDeCITA
        )
      );
    },
    isNumberRefRequired() {
      if (this.canContinueValidation) {
        return;
      }
      return (
        [
          screens.ManejoReferidos,
          screens.ReferidosAsistenciaTecnologica,
          screens.Historial_Terapias,
          screens.Historial_RegistroVisitas,
        ].includes(this.ticket.screen.id) &&
        (this.ticket.module.id === modules.PEI ||
          this.ticket.module.id === modules.HistorialTerapias ||
          this.ticket.module.id === modules.HistorialRegistroVisitas) &&
        !this.ticket.referenceNumberTypes.some(
          (c) => c.referenceType.id == referenceTypes.NumeroDeREFERIDO
        )
      );
    },
    isNumberRequestRequired() {
      if (this.canContinueValidation) {
        return;
      }

      return (
        [
          screens.SolicitudDeHorasCompensatorias,
          screens.SolicitudServiciosAsistenciaTecnologica,
          screens.DirectorioProveedores,
          screens.CrearNuevaSolicitud,
          screens.Entrar,
          screens.ImprimirSolicitud,
          screens.ImprimirCartaAprobacion,
          screens.VerHistorialContratos,
        ].includes(this.ticket.screen.id) &&
        (this.ticket.module.id === modules.PEI ||
          this.ticket.module.id === modules.SolicitudRP) &&
        !this.ticket.referenceNumberTypes.some(
          (c) => c.referenceType.id == referenceTypes.numeroDeSolicitud
        )
      );
    },
    validateTypeRefMessage() {
      if (this.isNumberRequestRequired) {
        return 'El número de solicitud es requerido.';
      }
      if (this.isNumberRefRequired) {
        return 'El número de referido es requerido.';
      }
      if (this.isNumberDateRequired) {
        return 'El número de cita es requerido.';
      }
      if (this.isDisciplineRequired) {
        return 'La disciplina es requerida.';
      }
      if (this.isEvaluationDisciplineRequired) {
        return 'La disciplina de evaluación es requerida.';
      }
      if (this.isQuerellaNumberRequired) {
        return 'El Número de Querella es requerido.';
      }
      if (this.sieIsrequired) {
        return 'El número de SIE es requerido.';
      }
      if (this.peiNumberIsRequired) {
        return 'El número de PEI es requerido.';
      }
      if (this.contractNumberRPRequired) {
        return 'El número de Contrato es requerido.';
      }
      if (this.serviceRequired) {
        return 'El servicio es requerido.';
      }
      if (this.consultaNumberRequired) {
        return 'El número de Consulta es requerido.';
      }
      if (this.hasNotReferenceNumberType) {
        return 'El número de referencia es requerido.';
      } else return '';
    },
    screens() {
      return (
        this.modules.find((module) => module.id === this.ticket.module?.id)
          ?.screens || []
      );
    },
    headerActions() {
      return [
        {
          text: 'Regresar',
          icon: 'fas fa-arrow-circle-left',
          variant: 'outline-success',
          click: this.goBack,
        },
        {
          text: 'Someter',
          icon: 'fas fa-flag',
          variant: 'success',
          click: this.onSaveClicked,
          visible: !this.isReadOnly,
        },
      ];
    },
    isReadOnly() {
      return this.readOnly || this.ticket.itemId !== null;
    },
    flatReferenceNumberTypes() {
      return this.ticket.referenceNumberTypes.map((r) => {
        return {
          id: r.id,
          referenceTypeId: r.referenceType.id,
          referenceTypeName: r.referenceType.name,
          referenceValue: r.referenceValue,
        };
      });
    },
    hasDocument() {
      return this.ticketData.documents.length > 0;
    },
    showSieDD() {
      return (
        this.newReferenceNumberType.referenceType?.id ==
          referenceTypes.NumeroDeSIE && this.application == application.PADRES
      );
    },
    studentsListFiltered() {
      return this.studentsList.filter(
        (c) =>
          !this.ticket.referenceNumberTypes.some(
            (x) => x.referenceValue == c.id
          )
      );
    },
  },
  watch: {
    documents: {
      immediate: true,
      handler() {
        if (this.documents.length === 0) return;
        for (let index = 0; index < this.documents.length; index++) {
          const document = this.documents[index];
          const fileUploadRef = this.$refs[`fileUpload-${document.tempId}`];
          if (fileUploadRef) fileUploadRef.fileLabel = document.name;
        }
      },
    },
    ticketData: {
      immediate: true,
      handler(value) {
        this.ticket = { ...value };

        if (this.ticket.email == null) {
          this.ticket.email = this.user.email;
        }
      },
    },
    'ticketData.userContactInformation': {
      immediate: true,
      handler(value) {
        if (value == null) {
          return;
        }

        this.ticket.userContactInformation = { ...value };

        this.ticket.email = this.ticket.userContactInformation.email
          ? this.ticket.userContactInformation.email
          : this.user.email;

        if (this.ticket.telephone1 == null) {
          this.ticket.telephone1 = this.ticket.userContactInformation.telephone1;
          this.ticket.telephone1Extension = this.ticket.userContactInformation.telephone1Ext;
        }

        if (this.ticket.telephone2 == null) {
          this.ticket.telephone2 = this.ticket.userContactInformation.telephone2;
          this.ticket.telephone2Extension = this.ticket.userContactInformation.telephone2Ext;
        }
      },
    },
    'ticket.module': {
      immediate: true,
      handler(value) {
        this.ticket.module = value;
      },
    },
  },
  methods: {
    addDocumentRow() {
      this.ticket.documents.push({
        tempId: Number(new Date()),
        id: null,
        data: null,
        name: null,
      });
    },
    removeDocumentRow(tempId) {
      if (this.ticket.documents.length <= 1) return;
      const index = this.documents.findIndex((x) => x.tempId === tempId);
      if (index !== -1) this.ticket.documents.splice(index, 1);
    },
    async onSaveClicked() {},
    async onReferenceNumberTypeAdded() {
      this.hasNotReferenceNumberType = false;

      const isValid = await this.$refs.refReferenceNumberAdded.validate();

      if (this.showSieDD) {
        this.newReferenceNumberType.referenceValue = this.newReferenceNumberType.student?.id;
      }

      if (isValid) {
        var { data } = await this.validateReferenceNumber(
          this.newReferenceNumberType.referenceValue,
          this.newReferenceNumberType.referenceType.id
        );
        if (!data) {
          this.ShowToast(
            'Atención',
            `El ${this.newReferenceNumberType.referenceType.name} ingresado no existe.`,
            'error'
          );
          return;
        }

        const array = this.ticket.referenceNumberTypes;

        this.referenceValueExists = array.some(
          (item) =>
            item.referenceType.id ==
              this.newReferenceNumberType.referenceType.id &&
            item.referenceType.name ==
              this.newReferenceNumberType.referenceType.name &&
            item.referenceValue == this.newReferenceNumberType.referenceValue
        );

        if (!this.referenceValueExists) {
          this.ticket.referenceNumberTypes.push(this.newReferenceNumberType);
          this.clearNewReferenceNumberType();
        }

        await this.$refs.refReferenceNumberAdded.reset();
      }
    },
    onReferenceNumberTypeDeleted(reference) {
      const array = this.ticket.referenceNumberTypes;

      this.ticket.referenceNumberTypes = array.filter(
        (item) =>
          (item.referenceType.id !== reference.referenceTypeId &&
            item.referenceType.name !== reference.referenceTypeName &&
            item.referenceValue !== reference.referenceValue) ||
          (item.referenceType.id === reference.referenceTypeId &&
            item.referenceType.name === reference.referenceTypeName &&
            item.referenceValue !== reference.referenceValue)
      );
    },
    clearNewReferenceNumberType() {
      this.newReferenceNumberType = {
        referenceType: null,
        referenceValue: null,
      };
    },
    async canSaveTicket() {
      this.hasNotReferenceNumberType = this.ticket.referenceNumberTypes == 0;

      if (this.hasNotReferenceNumberType) {
        await this.$refs.refReferenceNumberAdded.reset();
      }

      this.hasReferencesValidationErrors = this.validateTypeRefMessage != '';

      const isValidHeader = await this.$refs.refHeaderValidation.validate();

      const isValidFooter = await this.$refs.refFooterValidation.validate();

      return (
        isValidHeader &&
        isValidFooter &&
        !this.hasNotReferenceNumberType &&
        !this.hasReferencesValidationErrors
      );
    },
    goBack() {
      this.$router.go(-1);
    },
    async onModuleChange(val) {
      this.ticket.screen = null;
      this.newReferenceNumberType.referenceType = null;
      this.ticket.referenceNumberTypes = [];
      const moduleId = val ? val.id : null;

      this.$emit('update-references', { moduleId });
      await this.$refs.refReferenceNumberAdded.reset();
    },
    async onScreenChange(val) {
      this.newReferenceNumberType.referenceType = null;

      const moduleId = this.ticket.module.id;
      const screenId = val ? val.id : null;

      this.$emit('update-references', { moduleId, screenId });
      await this.$refs.refReferenceNumberAdded.reset();
    },
  },
};
</script>

<style lang="scss" scoped>
.manage-ticket {
  ::v-deep {
    .resolved-comment {
      --gray: #f7f7f7;
      padding: 1rem;
      border-radius: 0.25rem;
      background: var(--gray);
      margin-bottom: 2rem;
      .icon-view_extended {
        .dot {
          width: 1.5rem;
          height: 1.5rem;
          margin-right: 0.5rem;
          .title-icon {
            font-size: 1.3rem;
            color: #2db782;
          }
        }
      }
    }
    .canceled-comment {
      --gray: #f7f7f7;
      padding: 1rem;
      border-radius: 0.25rem;
      background: var(--gray);
      margin-bottom: 2rem;
      .icon-view_extended {
        .dot {
          width: 1.5rem;
          height: 1.5rem;
          margin-right: 0.5rem;
          .title-icon {
            font-size: 1.3rem;
            color: #b72d3f;
          }
        }
      }
    }
    .separator_extended {
      --separator-font-color: #707070;
      --separator-line-color: #b5c3bc;
      margin-top: 1rem;
      .separator-custom {
        --line-separator-color: var(--separator-line-color);
        .separator-custom__text {
          background: none;
          font-size: 1rem;
          color: var(--separator-font-color);
          font-weight: normal;
          padding: 0;
          cursor: default;
          user-select: none;
        }
      }
    }
  }
}

.frame-container {
  background: #f2f2f2;
  border-radius: 5px;
  padding: 15px 20px;
}

.section-label {
  font-size: 14px;
  color: black;
  font-weight: bold;
}

::v-deep div.chips-container {
  margin-bottom: 10px;
  padding-bottom: 0;
}

::v-deep div.chips-container .chips {
  margin-top: 0;
}

::v-deep .refNumbers > div:first-child {
  margin-top: 0 !important;
}

.allowed-files-message {
  font-size: 12px !important;
}

@media only screen and (max-width: 768px) {
  .btn-referencenumber-content {
    margin-top: 10px;
  }
}
</style>
